import { Grid, makeStyles } from '@material-ui/core'
import ConfirmationModal from 'components/common/ConfirmationModal'
import HoveredActions from 'components/common/HoveredActions'
import { Check2 } from 'components/common/icons'
import OpenInNewTabIcon from 'components/common/icons/OpenInNewTabIcon'
import NeedsAttentionReason from 'components/common/NeedsAttentionReason'
import ProfilePicture from 'components/common/ProfilePicture'
import DazzedParagraph14 from 'components/common/text/DazzedParagraph14'
import Tooltip from 'components/common/Tooltip'
import useCopilotAccountOwnerId from 'components/CopilotControl/hooks/useCopilotAccountOwnerId'
import useCopilotPatientsList from 'components/CopilotControl/hooks/useCopilotPatientsList'
import PatientStatus from 'components/Patients/PatientStatus'
import { ROUTES } from 'consts'
import useRolePermissions from 'hooks/useRolePermissions'
import useShowSnackbar from 'hooks/useShowSnackbar'
import moment from 'moment'
import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useResolveNeedsAttentionMutation } from 'store/modules/CopilotControl'
import { trackEvent } from 'utils/analyticsUtils'
import { getEnvironmentHostname } from 'utils/awsUtils'

const useStyles = makeStyles(() => ({
  patientItem: {
    position: 'relative',
    display: 'flex',
    padding: 20,
    alignItems: 'center',
    borderBottom: '1px solid #E9E9E9'
  },
  avatar: {
    width: '12%',
    maxWidth: 80
  },
  patientNameStatusContainer: {
    width: '21%',
    display: 'flex',
    flexDirection: 'column'
  },
  needsAttentionReasonContainer: {
    width: '22%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  },
  noteContainer: {
    width: '45%',
    paddingRight: 20
  },
  note: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  time: {
    color: '#95A4C1',
    position: 'absolute',
    bottom: 7,
    right: 15,
    fontSize: 11
  }
}))

const NeedsAttentionPatientItem = ({ patient, index }) => {
  const classes = useStyles()
  const { t } = useTranslation()
  const { permissions } = useRolePermissions()

  const { getPatientStatusObject } = useCopilotPatientsList()
  const accountOwnerId = useCopilotAccountOwnerId()
  const [resolveNeedsAttention, { isError, isSuccess }] = useResolveNeedsAttentionMutation()
  const {
    id: patientId,
    name: patientName,
    profilePicture,
    statusKey,
    program,
    needsAttentionData,
    needsAttentionTime
  } = patient

  useShowSnackbar({
    isOpen: isError || isSuccess,
    type: isError ? 'error' : 'success',
    message: isError
      ? t('pages.copilotControl.widgets.needsAttention.snackbars.error')
      : t('pages.copilotControl.widgets.needsAttention.snackbars.success')
  })

  const [isConfirmationOpen, setIsConfirmationOpen] = React.useState(false)

  const handleResolveNeedsAttentionClicked = useCallback(() => {
    trackEvent('Copilot Control - Needs attention patient - Resolve confirmation opened')
    setIsConfirmationOpen(true)
  }, [])

  const handleConfirmResolve = useCallback(() => {
    trackEvent('Copilot Control - Needs attention patient - Resolve clicked', {
      patientId: patient.id
    })
    resolveNeedsAttention({ accountOwnerId, patientId: patient.id, needsAttentionReason: needsAttentionData?.reason })
    setIsConfirmationOpen(false)
  }, [accountOwnerId, needsAttentionData, patient, resolveNeedsAttention])

  const handleCancelResolve = useCallback(() => {
    trackEvent('Copilot Control - Needs attention patient - Resolve cancelled')
    setIsConfirmationOpen(false)
  }, [])

  const actions = useMemo(
    () =>
      [
        {
          key: 'openPatient',
          icon: <OpenInNewTabIcon />,
          tooltip: t('pages.copilotControl.widgets.needsAttention.actions.openPatient'),
          onClick: () => {
            trackEvent('Copilot Control - Patient opened from list', {
              view: 'Needs Attention',
              patientId
            })
            window.open(`${getEnvironmentHostname()}${ROUTES.PATIENTS}/${patientId}`, '_blank')
          },
          condition: true
        },
        {
          key: 'resolve',
          icon: <Check2 />,
          tooltip: t('pages.copilotControl.widgets.needsAttention.actions.resolve'),
          onClick: handleResolveNeedsAttentionClicked,
          condition: permissions.needsAttentionResolving
        }
      ].filter(action => action.condition),
    [handleResolveNeedsAttentionClicked, patientId, permissions.needsAttentionResolving, t]
  )

  const [isHovered, setIsHovered] = React.useState(false)

  const patientStatus = getPatientStatusObject(statusKey, program)

  return (
    <>
      <Grid
        key={index}
        container
        item
        className={classes.patientItem}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <Grid item className={classes.avatar}>
          <ProfilePicture name={patientName} photo={profilePicture} />
        </Grid>
        <Grid item className={classes.patientNameStatusContainer}>
          <Grid item>
            <DazzedParagraph14 className={classes.patientNameText}>{patientName}</DazzedParagraph14>
          </Grid>
          <Grid item>
            <PatientStatus status={patientStatus} />
          </Grid>
        </Grid>
        <Grid item className={classes.needsAttentionReasonContainer}>
          <NeedsAttentionReason reason={needsAttentionData?.reason} shortened />
        </Grid>
        <Grid item className={classes.noteContainer}>
          <Tooltip value={needsAttentionData?.note} placement="top" arrow variant="light">
            <div className={classes.note}>{needsAttentionData?.note}</div>
          </Tooltip>
        </Grid>
        {needsAttentionTime && <div className={classes.time}>{moment(needsAttentionTime).fromNow()}</div>}
        <HoveredActions actions={actions} isOpen={isHovered} placement="top" />
      </Grid>
      <ConfirmationModal
        isOpen={isConfirmationOpen}
        title={t('pages.copilotControl.widgets.needsAttention.resolveConfirmation.description')}
        onConfirm={handleConfirmResolve}
        onClose={handleCancelResolve}
        acceptButtonText={t('general.confirm')}
        declineButtonText={t('general.cancel')}
      />
    </>
  )
}

export default NeedsAttentionPatientItem
